import { Table } from "@tanstack/react-table";
import { Button } from "../ui/button";
import {
    OptimizationButtonVariant,
    Product,
    ProductStatus,
} from "@/types/Products";
import { Play, Square } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import {
    AlertDialog,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { LoadingButtonContent } from "../ui/loading-button-content";

export default function OptimizationButton({
    variant,
    table,
    onClose,
    selectedProducts,
}: {
    variant: OptimizationButtonVariant;
    table: Table<Product>;
    onClose: () => void;
    selectedProducts: Product[];
}) {
    const buttonText = variant === "start" ? "Start" : "Stop";
    const buttonVariant = variant === "start" ? "default" : "outline";
    const buttonIcon = variant === "start" ? <Play /> : <Square />;
    const buttonStatus = variant === "start" ? "Optimizing" : "Paused";

    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const { session } = useAuth();

    const products = selectedProducts;

    const setOptimizationStatus = async (status: ProductStatus) => {
        setIsLoading(true);
        const updatedProducts = products.map((product) => {
            const originalProduct = product.original || product;

            return {
                shopify_variant_id: originalProduct.shopify_variant_id,
                status,
                user_id: session?.user.id,
                reset_scheduled: status === "Paused" ? true : false,
            };
        });

        const { error } = await supabase
            .from("products")
            .upsert(updatedProducts);

        if (error) {
            console.error(error);
        }
        setIsLoading(false);
        setOpen(false);
        onClose();
    };

    const getProductSummary = () => {
        const maxProducts = 3;
        const maxLength = 200; // Maximum characters before truncating
        const productNames = products.map(
            (p) => p.product_name || "Unnamed product"
        );

        let summary = "";
        if (productNames.length <= maxProducts) {
            summary = productNames.join(", ");
        } else {
            summary = `${productNames.slice(0, maxProducts).join(", ")} and ${
                productNames.length - maxProducts
            } more`;
        }

        return summary.length > maxLength
            ? summary.slice(0, maxLength) + "..."
            : summary;
    };

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    variant={buttonVariant}
                    disabled={selectedProducts.length === 0}
                >
                    {buttonIcon}
                    {buttonText} ({selectedProducts.length})
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {variant === "start" && "Start Optimization"}
                        {variant === "stop" && "Stop Optimization"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {variant === "start" &&
                            `This will start optimization for ${
                                products.length
                            } product${
                                products.length === 1 ? "" : "s"
                            }: ${getProductSummary()}`}
                        {variant === "stop" &&
                            `This will stop optimization for ${
                                products.length
                            } product${
                                products.length === 1 ? "" : "s"
                            }: ${getProductSummary()}`}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <Button
                        onClick={() => setOptimizationStatus(buttonStatus)}
                        className="w-[100px]"
                        disabled={isLoading}
                    >
                        <LoadingButtonContent loading={isLoading}>
                            Continue
                        </LoadingButtonContent>
                    </Button>
                    {/* </AlertDialogAction> */}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
